import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, WritableSignal, effect, forwardRef, signal } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, take, takeUntil } from 'rxjs';
import { FormControlPipe, TranslatePipe } from '@newroom-connect/library/pipes';
import { ActionRole, ButtonType, TranslatableInputType } from '@newroom-connect/library/enums';
import { ArrayHelper } from '@newroom-connect/library/helpers';
import { AutoTranslateService, ProjectService } from '@newroom-connect/library/services';
import { TranslationElement } from '@newroom-connect/library/types';

import { AutoTranslateComponent, IAutoTranslateParams } from '../../auto-translate/auto-translate.component';
import { ButtonComponent } from '../../buttons';
import { InputLanguageSelectorComponent, InputLanguageSelectorTextSize } from '../input-language-selector/input-language-selector.component';
import { InputComponent } from '../input.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormControlPipe,
    ButtonComponent,
    AutoTranslateComponent,
    InputLanguageSelectorComponent
  ],
  selector: 'nrc-input-translatable-text',
  templateUrl: './input-translatable-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTranslatableTextComponent),
      multi: true
    }
  ]
})
export class InputTranslatableTextComponent extends InputComponent<TranslationElement<string | null>> implements OnChanges {
  @Input({ required: true }) public languageCodes!: string[];
  @Input() public maxLength: string | number | null = 256;
  @Input() public type: TranslatableInputType = TranslatableInputType.TEXT_AREA;
  @Input() public selectedLanguageCode?: WritableSignal<string>;

  @Output() public languageCodeChangedEvent = new EventEmitter<string>();

  public translatableFormControls: Record<string, FormControl<string | null>> = {};
  public languageCodeFormControl = new FormControl<string>('', { nonNullable: true });
  public isTranslationModalOpen = signal<boolean>(false);
  public currentLanguageCode = signal<string>('');

  public actionRole = ActionRole;
  public buttonType = ButtonType;
  public translatableInputType = TranslatableInputType;
  public inputLanguageSelectorTextSize = InputLanguageSelectorTextSize;

  /**
   * @constructor
   *
   * @param autoTranslateService
   * @param projectService
   */
  constructor(
    private readonly autoTranslateService: AutoTranslateService,
    private readonly projectService: ProjectService
  ) {
    super();

    // If the selected language changed externally, update the form control to reflect the new language.
    effect(() => {
      if (!this.selectedLanguageCode) {
        return;
      }

      const currentLanguageCode = this.selectedLanguageCode();

      if (currentLanguageCode !== this.languageCodeFormControl.value) {
        this.currentLanguageCode.set(currentLanguageCode);
        this.languageCodeFormControl.patchValue(currentLanguageCode);
      }
    }, { allowSignalWrites: true });
  }

  /**
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['formControl']) {
      this.initializeFormControls();
      this.registerFormControlsChange();
    }
  }

  /**
   * Initializes the translatable form controls for each language code.
   *
   * Iterates over the language codes and creates a new form control for each one,
   * using the corresponding value from the `formControl` as the initial value, or an empty string if none is provided.
   */
  public initializeFormControls(): void {
    if (!ArrayHelper.isNotEmpty(this.languageCodes)) {
      return;
    }

    const [defaultLanguageCode] = this.languageCodes;

    this.languageCodeFormControl.patchValue(defaultLanguageCode);
    this.currentLanguageCode.set(defaultLanguageCode);

    this.translatableFormControls = this.translatableFormControls ?? {};
    const formControlValue = this.formControl.value ?? {};

    for (const languageCode of this.languageCodes) {
      this.translatableFormControls[languageCode] = new FormControl<string>(formControlValue[languageCode] ?? '');
    }
  }

  /**
   * Registers value change listeners for translatable form controls.
   * When the value changes for one of the translatable form controls,
   * it updates the corresponding language code in the main form control value.
   *
   */
  public registerFormControlsChange(): void {
    if (!this.translatableFormControls) {
      return;
    }

    for (const languageCode of this.languageCodes) {
      this.translatableFormControls[languageCode].valueChanges.pipe(
        debounceTime(200),
        takeUntil(this.destroy$)
      ).subscribe((value) => {
        const oldFormControlValue = this.formControl.value as TranslationElement<string | null>;

        oldFormControlValue[languageCode] = value;

        this.formControl.patchValue(oldFormControlValue);
        this.writeValue(this.formControl.value);
      });
    }
  }

  /**
   *
   * @param input
   * @param input.from
   * @param input.to
   */
  public handleAutoTranslateResult(input: IAutoTranslateParams): void {
    const { sourceLanguage, targetLanguage } = input;

    if (sourceLanguage === this.languageCodeFormControl.value) {
      const translationParams = {
        from: sourceLanguage,
        to: targetLanguage,
        text: input.text ?? ''
      };

      const currentProject = this.projectService.watchCurrentProject()();

      if (!currentProject || !translationParams.text) {
        return;
      }

      this.autoTranslateService.translateText(currentProject.id, translationParams).pipe(take(1)).subscribe(result => {
        this.translatableFormControls[targetLanguage].patchValue(result.text);

        this.languageCodeFormControl.patchValue(targetLanguage, { emitEvent: true });

        this.currentLanguageCode.set(targetLanguage);
      });
    }

    this.toggleAutoTranslateModal();
  }

  /**
   *
   */
  public toggleAutoTranslateModal(): void {
    this.isTranslationModalOpen.update(isOpen => !isOpen);
  }
}
